import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", description: "", image_path: null, featured: false };

export default function AdminCategories() {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null); // category being edited or empty

  const load = () => api.get("/categories").then((r) => setList(r.data || []));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { name: edit.name, description: edit.description || "", image_path: edit.image_path || null, featured: !!edit.featured };
    try {
      if (edit.id) await api.put(`/admin/categories/${edit.id}`, payload);
      else await api.post("/admin/categories", payload);
      toast.success("Categoria salva");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const del = async (c) => {
    if (!window.confirm(`Excluir "${c.name}"?`)) return;
    await api.delete(`/admin/categories/${c.id}`);
    toast.success("Removida");
    load();
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="overline-label">Taxonomia</div>
          <h1 className="font-display text-4xl text-white tracking-tighter mt-3">Categorias</h1>
        </div>
        <Button onClick={() => setEdit({ ...empty })} data-testid="new-cat-btn" className="bg-white text-black hover:bg-zinc-200 uppercase tracking-widest text-xs h-11 px-5">
          <Plus size={14} className="mr-2" /> Nova categoria
        </Button>
      </div>

      <div className="border border-white/10 bg-[#0A0A0C]">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Nome</th>
              <th className="text-left px-4 py-3">Slug</th>
              <th className="text-left px-4 py-3">Destaque</th>
              <th className="text-right px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id} className="border-t border-white/5">
                <td className="px-4 py-3 text-white">{c.name}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-500">{c.slug}</td>
                <td className="px-4 py-3 text-zinc-300">{c.featured ? "Sim" : "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEdit({ ...c })} className="bg-transparent border-white/15 text-white hover:bg-white/5 h-8 px-3" data-testid={`edit-cat-${c.slug}`}><Pencil size={14} /></Button>
                    <Button size="sm" variant="outline" onClick={() => del(c)} className="bg-transparent border-red-500/40 text-red-300 hover:bg-red-500/10 h-8 px-3" data-testid={`delete-cat-${c.slug}`}><Trash2 size={14} /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {list.length === 0 && (<tr><td colSpan="4" className="px-4 py-10 text-center text-zinc-500">Nenhuma categoria.</td></tr>)}
          </tbody>
        </table>
      </div>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="bg-[#0A0A0C] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>{edit?.id ? "Editar categoria" : "Nova categoria"}</DialogTitle>
          </DialogHeader>
          {edit && (
            <div className="space-y-4">
              <div>
                <label className="text-xs text-zinc-400">Nome *</label>
                <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="bg-[#050505] border-white/15 h-11 mt-1" data-testid="cat-name-input" />
              </div>
              <div>
                <label className="text-xs text-zinc-400">Descrição</label>
                <Textarea value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} rows={3} className="bg-[#050505] border-white/15 mt-1" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-300">Em destaque</span>
                <Switch checked={!!edit.featured} onCheckedChange={(v) => setEdit({ ...edit, featured: v })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)} className="bg-transparent border-white/15 text-white">Cancelar</Button>
            <Button onClick={save} className="bg-white text-black hover:bg-zinc-200" data-testid="save-cat-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
